import engineClient from '@/api/engineClient'
import { BIRTH_RANGE_TIMEOUT_MS } from '@/api/timeouts'
import { isEngineBatchPayload, type EngineItemError } from '@/utils/engineErrors'

export type EngineTagStatus = 'birthed' | 'tagged'

export type EngineTagRecord = {
  rfid_code: string
  sku_code: string | null
  status: EngineTagStatus
  country_code: string
  month_year: string
  serial: string
  tagged_at?: string | null
  birthed_at?: string | null
  created?: boolean
  already_existed?: boolean
}

export type BirthResult = EngineTagRecord & {
  created: boolean
  already_existed: boolean
}

export type TagResult = {
  rfid_code: string
  sku_code: string
  status: 'tagged'
  country_code: string
  month_year: string
  serial: string
  tagged_at: string | null
  remapped?: boolean
  previous_sku_code?: string | null
  created?: boolean
}

export type BatchLookupResult = {
  found: EngineTagRecord[]
  missing: string[]
}

export type SkuLookupResult = {
  sku_code: string
  rfid_codes: string[]
}

export type BirthBatchResponse = {
  results: BirthResult[]
  errors: EngineItemError[]
}

export type BirthRangeResult = {
  start_rfid_code: string
  end_rfid_code: string
  country_code: string
  month_year: string
  expected_count: number
  created: number
  already_existed: number
  already_tagged: number
  already_tagged_samples: string[]
}

export type TagBatchResponse = {
  results: TagResult[]
  errors: EngineItemError[]
}

function splitCodes(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[\s,;]+/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean)
    ),
  ]
}

function asBatchFromError<T>(err: unknown): { results: T[]; errors: EngineItemError[] } | null {
  if (!isEngineBatchPayload(err)) return null
  return {
    results: Array.isArray(err.results) ? (err.results as T[]) : [],
    errors: err.errors,
  }
}

export const engineService = {
  async birthOne(rfidCode: string): Promise<BirthResult> {
    const { data } = await engineClient.post<{ data: BirthResult }>('/api/v1/tags/birth', {
      rfid_code: rfidCode.trim().toUpperCase(),
    })
    return data.data
  },

  async birthMany(rfidCodes: string[]): Promise<BirthBatchResponse> {
    const codes = splitCodes(rfidCodes.join('\n'))
    try {
      const { data } = await engineClient.post<BirthBatchResponse>('/api/v1/tags/birth', {
        rfid_codes: codes,
      })
      return { results: data.results ?? [], errors: data.errors ?? [] }
    } catch (err) {
      const batch = asBatchFromError<BirthResult>(err)
      if (batch) return batch
      throw err
    }
  },

  async birthRange(body: {
    start_rfid_code?: string
    end_rfid_code?: string
    start_serial?: string
    end_serial?: string
    country_code?: string
    month_year?: string
  }): Promise<BirthRangeResult> {
    const { data } = await engineClient.post<{ data: BirthRangeResult }>(
      '/api/v1/tags/birth-range',
      body,
      { timeout: BIRTH_RANGE_TIMEOUT_MS }
    )
    return data.data
  },

  async tagOne(rfidCode: string, skuCode: string): Promise<TagResult> {
    const { data } = await engineClient.post<{ data: TagResult }>('/api/v1/tags/tag', {
      rfid_code: rfidCode.trim().toUpperCase(),
      sku_code: skuCode.trim(),
    })
    return data.data
  },

  async tagMany(pairs: Array<{ rfid_code: string; sku_code: string }>): Promise<TagBatchResponse> {
    try {
      const { data } = await engineClient.post<TagBatchResponse>('/api/v1/tags/tag', {
        tags: pairs,
      })
      return { results: data.results ?? [], errors: data.errors ?? [] }
    } catch (err) {
      const batch = asBatchFromError<TagResult>(err)
      if (batch) return batch
      throw err
    }
  },

  async lookupByRfid(rfidCode: string): Promise<EngineTagRecord> {
    const { data } = await engineClient.get<{ data: EngineTagRecord }>(
      `/api/v1/lookup/rfid/${encodeURIComponent(rfidCode.trim().toUpperCase())}`
    )
    return data.data
  },

  async lookupBySku(skuCode: string): Promise<SkuLookupResult> {
    const { data } = await engineClient.get<{ data: SkuLookupResult }>(
      `/api/v1/lookup/sku/${encodeURIComponent(skuCode.trim())}`
    )
    return data.data
  },

  async lookupBatch(rfidCodes: string[]): Promise<BatchLookupResult> {
    const codes = splitCodes(rfidCodes.join('\n'))
    const { data } = await engineClient.post<BatchLookupResult>('/api/v1/lookup/rfid/batch', {
      rfid_codes: codes,
    })
    return data
  },

  splitCodes,
}

export type { EngineItemError }
